import React, { useState } from "react";
import { useRecoilState, useRecoilValue } from "recoil";
import MaterialTableTS from "../../../../components/tables/MaterialTableTS";
import { CSVLink } from "react-csv";
import useGetLeasedEquipmentReport from "./hooks/useGetLeasedEquipmentReport";
import { mainSelectedTab } from "../../atoms";
import { useHistory } from "react-router-dom";
import { permissionsSelector } from "../../../Login/selectors/permissionSelector";
import { leasedEquipmentsReports } from "../../OfficeSettings/atoms/atoms";
import { PrinterIcon } from "@heroicons/react/24/solid";
import { OfficeTabs } from "../../../OfficeTab/OfficeTabs";
import DownloadButton from "../../../../components/DownloadButton";

interface LeasedEquipmentReportProps {}
export const LeasedEquipmentReport: React.FC<
  LeasedEquipmentReportProps
> = () => {
  const searchInputsData = {
    RecordId: null,
    OfficeId: "",
    Issuer: "",
    IssuerName: "",
    IssuerPhone: "",
    Type: "",
    LicenseOrPermitNumber: "",
    IssuedOrRenewed: "",
    ExpirationDate: "",
    IsActive: "",
    Notes: "",
    CreatedDate: "",
    CreatedBy: "",
    ModifiedDate: "",
    ModifiedBy: "",
    OfficeTypes:
      "'Retail Office','Web Office','Call Center','Operations', 'Retail'",
    LicenseTypes: "'Business License'",
    LegalEntity:
      "'A-Affordable Insurance Agency, Inc.','Freeway Insurance Services, Inc.','Freeway Insurance Services of Arizona, Inc.','Freeway Insurance Services of Nevada, Inc.','Freeway Insurance Services of Texas, Inc.','Freeway Managing General Agency', 'Freeway Insurance Services America, LLC'",
  };

  const { data: leasedEquipments, isLoading: leasedEquipmentsLoading } =
    useGetLeasedEquipmentReport(searchInputsData);
  const [, setSelectedIndex] = useRecoilState(mainSelectedTab);
  const history = useHistory();
  const userPermissions = useRecoilValue(permissionsSelector);

  interface ILeasedEquipments {
    officeName: string;
    vendorStorageName: string;
    officeStatus: string;
    brand: string;
    stateCode: string;
    officeType: string;
    typeName: string;
    serialNumber: string;
    makeName: string;
    model: string;
    leaseStartDate: string;
    leaseEndDate: string;
    leaseNumber: string;
    leaseAmount: string;
    modelDescription: string;
  }

  //Format Date
  function formatDate(date) {
    var d = new Date(date),
      month = "" + (d.getMonth() + 1),
      day = "" + d.getDate(),
      year = d.getFullYear();

    if (month.length < 2) month = "0" + month;
    if (day.length < 2) day = "0" + day;
    return [month, day, year].join("/");
  }

  //For downloading table data
  const exportdata = leasedEquipments?.data?.map((lsde: ILeasedEquipments) => {
    return {
      "Office Name": lsde.officeName,
      "Vendor Name": lsde.vendorStorageName,
      "Office Status": lsde.officeStatus,
      Brand: lsde.brand,
      State: lsde.stateCode,
      "Office Type": lsde.officeType,
      Type: lsde.typeName,
      "Equipment ID/Serial Number": lsde.serialNumber,
      Make: lsde.makeName,
      "Model/Version":
        lsde?.makeName === "Quadient" || lsde?.makeName === "Pitney Bows"
          ? lsde?.model + " (" + lsde?.modelDescription + ")"
          : lsde?.model,
      "Lease Start Date": lsde.leaseStartDate
        ? formatDate(lsde.leaseStartDate)
        : "",
      "Lease End Date": lsde.leaseEndDate ? formatDate(lsde.leaseEndDate) : "",
      "Lease Number": lsde.leaseNumber,
      "Lease Amount": lsde.leaseAmount,
    };
  });

  let dateTimeToday = new Date(new Date().toLocaleString()).toLocaleString();
  const csvReport = {
    filename: "LeasedEquipments_" + dateTimeToday + ".csv",
    data: exportdata ? exportdata : [],
  };

  const matColumns = [
    {
      title: "Office Name",
      field: "officeName",
      render: (rowData) => {
        return (
          <div
            className="cursor-pointer"
            onClick={() => {
              const url = "/officesettings/officesetting";
              history.push({
                pathname: url,
                state: {
                  officeId: rowData?.officeId,
                  action: "LeasedEquipmentReports",
                },
              });
              window.location.reload();
              let userTabs = [
                {
                  to: "",
                },
              ];
              [...OfficeTabs].forEach((tabs) => {
                let temp = [...userPermissions].filter(
                  (user) => user === tabs.permission
                );
                if (temp.length > 0)
                  userTabs.push({
                    to: tabs.to,
                  });
              });
              userTabs.shift();
              const index = userTabs.findIndex((c) => c.to === url);
              setSelectedIndex(index);
            }}
          >
            <span className="text-quinary  font-bold underline text-sm ">
              {rowData.officeName}
            </span>
          </div>
        );
      },
    },
    {
      title: "Vendor Name",
      field: "vendorStorageName",
      render: (rowData) => {
        return (
          <div
            className="cursor-pointer"
            onClick={() => {
              const url = "/vendortab/vendor";
              history.push({
                pathname: url,
                state: {
                  vendorId: rowData?.vendorStorageId,
                  action: "LeasedEquipmentReports",
                },
              });
              window.location.reload();
              let userTabs = [
                {
                  to: "",
                },
              ];
              [...OfficeTabs].forEach((tabs) => {
                let temp = [...userPermissions].filter(
                  (user) => user === tabs.permission
                );
                if (temp.length > 0)
                  userTabs.push({
                    to: tabs.to,
                  });
              });
              userTabs.shift();
              const index = userTabs.findIndex((c) => c.to === url);
              setSelectedIndex(index);
            }}
          >
            <span className="text-quinary  font-bold underline text-sm ">
              {rowData.vendorStorageName}
            </span>
          </div>
        );
      },
    },
    {
      title: "Office Status",
      field: "officeStatus",
    },
    {
      title: "Brand",
      field: "brand",
    },
    {
      title: "State",
      field: "stateCode",
    },
    {
      title: "Office Type",
      field: "officeType",
    },
    {
      title: "Type",
      field: "typeName",
    },
    {
      title: "Equipment ID/Serial Number",
      field: "serialNumber",
    },
    {
      title: "Make",
      field: "makeName",
    },
    {
      title: "Model/Version",
      field: "model",
      render: (rowData) =>
        // rowData.model
        //   ? rowData?.make === "Quadient" || rowData?.make === "Pitney Bows"
        //     ? rowData?.model + " (" + rowData?.modelDescription + ")"
        //     : rowData?.model
        //   : "",
        rowData?.modelDescription
          ? rowData?.model + " (" + rowData?.modelDescription + ")"
          : rowData?.model,
    },
    {
      title: "Lease Start Date",
      field: "leaseStartDate",
      render: (rowData) =>
        rowData.leaseStartDate
          ? new Date(rowData.leaseStartDate).toLocaleDateString()
          : "",
    },
    {
      title: "Lease End Date",
      field: "leaseEndDate",
      render: (rowData) =>
        rowData.leaseEndDate
          ? new Date(rowData.leaseEndDate).toLocaleDateString()
          : "",
    },
    {
      title: "Lease Number",
      field: "leaseNumber",
    },
  ];

  return (
    <div className="py-4 px-8 items-center">
      <div className="">
        <div className="overflow-x-auto">
          <div>
            <div className="flex gap-2 items-center">
              <div>
                <PrinterIcon className="text-white bg-blue-500 rounded-md h-7 w-7 p-1" />
              </div>
              <div className="flex flex-col leading tight">
                <h3 className="text-xs font-medium text-gray-500">
                  Reports Module
                </h3>
                <h1 className="mr-auto font-bold text-primary text-md uppercase">
                  LEASED EQUIPMENT REPORT
                </h1>
              </div>
            </div>
          </div>
          <div className="mt-2 " />
          <div className="flex justify-between">
            <div>
              <span className="text-gray-500 text-sm">Total Records: </span>
              <span className="text-primary text-base font-semibold">
                {leasedEquipments?.data?.length}
              </span>
            </div>

            <CSVLink {...csvReport}>
              <DownloadButton
                buttonValue="Download File"
                onClick={() => exportdata()}
                className={undefined}
                disabled={undefined}
              />
            </CSVLink>
          </div>
        </div>
        <div className="mt-2 mb-4 h-1 w-full border-b-2 border-secondary " />
        <div className="overflow-x-auto grid grid-row box-shadow">
          <MaterialTableTS
            data={leasedEquipments?.data}
            isLoading={leasedEquipmentsLoading}
            columns={matColumns}
            showTitle={false}
          />
        </div>
      </div>
    </div>
  );
};
